<template>
	<section>
		<header-page
			actions
			name="account-definitions"
			:title="title"
			@run-action="runAction"
		/>
		<account-definitions-list
			:items-total-quantity="itemsTotalQuantity"
			:items-quantity="rows.length"
			:current-page="params.page"
			:last-page="lastPage"
			:rows="rows"
			@click-icon="clickIcon"
			@on-page="onPage"
			@on-search="onSearch"
		>
		</account-definitions-list>
		<action-bar
			class="action-mobile"
			:resource-uri="$route.name"
			@run-action="runAction"
		/>
		<action-sidebar
			:title="action.title"
			:value="action.visible"
			@close-modal="closeModal"
		>
			<account-definitions-form
				v-if="isAccountDefinitionForm"
				:subsidiary-id="selectedRow.subsidiaryId"
				:customer-id="selectedRow.customerId"
				:id="selectedRow.id"
				@close-modal="closeModal"
			/>
			<account-definitions-remove
				v-if="isAccountDefinitionRemove"
				:id="selectedRow.id"
				:name="selectedRow.name"
				@close-modal="closeModal"
			/>
			<rules-assign-form
				v-if="isAccountRulesAssign"
				:subsidiary-id="subsidiaryId"
				:definition-id="definitionId"
				:module-id="moduleId"
				@close-modal="closeModal"
			/>
			<import-accounting-definitions
				v-if="isImportAccountingDefinitions"
				@load-data="loadData"
				@close-modal="closeModal"
			/>
		</action-sidebar>
	</section>
</template>

<script>
import { ACCOUNTING_DEFINITION, ACCOUNTING_DEFINITION_REMOVE, ACCOUNTING_RULES_ASSIGN, IMPORT_ACCOUNTING_DEFINITION } from '@/shared/action-codes';
import actionBar from '@/components/shared/action-bar/action-bar';
import accountDefinitionsList from '@/components/account-definitions/account-definitions-list';
import importAccountingDefinitions from '@/components/account-definitions/import-accounting-definitions';
import actionSidebar from '@/components/shared/action-sidebar';
import accountDefinitionsForm from '@/components/account-definitions/account-definitions-form';
import accountDefinitionsRemove from '@/components/account-definitions/account-definitions-remove';
import rulesAssignForm from '@/components/rules/rules-assign-form';
import { mapGetters } from 'vuex';

async function loadData() {
	this.params.customerId = this.customerId || null;
	this.params.subsidiaryId = this.subsidiaryId || null;
	this.selectedRow.customerId = this.customerId;
	this.selectedRow.subsidiaryId = this.subsidiaryId;
	const { data: rows, headers } = await this.$http.get('accounting-definitions', { params: this.params });
	this.rows = rows.map((dc) => {
		const newDc = {
			id: dc.id,
			name: dc.name,
			code: dc.code,
			customerId: dc.customerId,
			moduleName: dc.module ? dc.module.name : '',
			subsidiaryId: dc.subsidiaryId,
			moduleId: dc.moduleId,
		};
		return newDc;
	});
	this.lastPage = Number(headers['x-last-page']);
	this.itemsTotalQuantity = Number(headers['x-quantity']);
	return this.rows;
}

function closeModal(reloadList) {
	this.action.code = '';
	this.action.title = '';
	this.action.visible = false;
	this.selectedRow.id = 0;
	if (reloadList) {
		loadData.call(this);
	}
}

function clickIcon({ action, row }) {
	this.selectedRow.customerId = row.customerId;
	this.selectedRow.subsidiaryId = row.subsidiaryId;
	this.selectedRow.id = row.id;
	this.selectedRow.name = row.name;
	if (action === 'edit') {
		this.action.title = `Editar - ${row.name}`;
		this.action.code = ACCOUNTING_DEFINITION;
	} else if (action === 'delete') {
		this.action.code = ACCOUNTING_DEFINITION_REMOVE;
		this.action.title = `Eliminar - ${row.name}`;
	} else if (action === 'rules') {
		this.definitionId = row.id;
		this.moduleId = row.moduleId;
		this.action.title = `${row.name} / Asignar Reglas`;
		this.action.code = ACCOUNTING_RULES_ASSIGN;
	}
	this.action.visible = true;
}

async function created() {
	await loadData.call(this);
}

function customerId() {
	return Number(this.$route.query.customerId);
}

function isAccountDefinitionForm() {
	return this.action.code === ACCOUNTING_DEFINITION;
}

function isAccountDefinitionRemove() {
	return this.action.code === ACCOUNTING_DEFINITION_REMOVE;
}

function isAccountRulesAssign() {
	return this.action.code === ACCOUNTING_RULES_ASSIGN;
}

function isImportAccountingDefinitions() {
	return this.action.code === IMPORT_ACCOUNTING_DEFINITION;
}

function onPage(page) {
	this.params.page = page;
	loadData.call(this);
}

function onSearch(search) {
	this.params.page = 1;
	this.params.search = search;
	loadData.call(this);
}

function runAction({ name, code }) {
	this.action.title = name;
	this.action.code = code;
	this.action.visible = true;
	this.selectedRow.id = 0;
}

function subsidiaryId() {
	return Number(this.$route.query.subsidiaryId);
}

function title() {
	return `${this.$route.query.name} / ${this.$route.meta.title}`;
}

function currentSubsidiary(newSub) {
	const { id, name, customerId: idCustomer } = newSub;
	const newName = name.split(' ').join('-');
	this.goTo(
		'account-definitions',
		{ query: { subsidiaryId: id, customerId: idCustomer, name: newName } },
	);
}

function routeHandler() {
	loadData.call(this);
}

function data() {
	return {
		action: {
			code: '',
			title: '',
			visible: false,
		},
		definitionId: null,
		itemsTotalQuantity: null,
		lastPage: 1,
		params: {
			customerId: null,
			limit: 10,
			page: 1,
			search: null,
			subsidiaryId: null,
		},
		selectedRow: {
			id: null,
			customerId: null,
			subsidiaryId: null,
		},
		rows: [],
		moduleId: null,
	};
}


export default {
	name: 'page-account-definitions',
	created,
	components: {
		actionBar,
		accountDefinitionsForm,
		accountDefinitionsList,
		accountDefinitionsRemove,
		actionSidebar,
		rulesAssignForm,
		importAccountingDefinitions,
	},
	computed: {
		...mapGetters('subsidiary', [
			'currentSubsidiary',
		]),
		customerId,
		isAccountDefinitionForm,
		isAccountDefinitionRemove,
		isAccountRulesAssign,
		isImportAccountingDefinitions,
		subsidiaryId,
		title,
	},
	data,
	methods: {
		clickIcon,
		closeModal,
		onPage,
		onSearch,
		runAction,
		loadData,
	},
	watch: {
		currentSubsidiary,
		'$route.params': routeHandler,
	},
};
</script>



// WEBPACK FOOTER //
// src/pages/account-definitions/page-account-definitions.vue